/**
 * Shared between `absorb()`'s post-drop flight and `carry-into.ts`'s pickup
 * lift — both promote a real node out of normal layout (onto `position:
 * fixed`, then the popover top layer) and both need the same two things to
 * do it without visible damage: a snapshot of the styles that promotion
 * would otherwise disturb, and a same-box placeholder that holds the gap
 * open while the real node is gone from flow. What differs between the two
 * callers (the popover-already-open decision, the rest of the promotion
 * dance, everything about what happens once the node is lifted) stays in
 * each file — only this narrow, identical piece is shared.
 */

export interface LiftPreflight {
	alignSelf: string
	background: string
	border: string
	/** Read only by `absorb()`'s own flight keyframes — carry-into's pickup lift never needs it. */
	borderRadius: string
	color: string
	display: string
	flex: string
	gridArea: string
	justifySelf: string
	margin: string
	order: string
	overflow: string
	padding: string
}

/**
 * The computed styles a promoted element must reassert once it's
 * `position: fixed` and (once popover-open) inline with the `[popover]` UA
 * stylesheet's own reset of padding/border/background/color/overflow —
 * which wins on specificity ties over an author's own class-driven values —
 * plus the layout-participation properties (`margin`/`alignSelf`/
 * `justifySelf`/`gridArea`/`flex`/`order`) `createLayoutSeat()` below carries
 * onto the seat, so the container the element left doesn't reflow around it.
 */
export function capturePreflightStyle(element: HTMLElement): LiftPreflight {
	const computed = getComputedStyle(element)
	return {
		alignSelf: computed.alignSelf,
		background: computed.background,
		border: computed.border,
		borderRadius: computed.borderRadius,
		color: computed.color,
		display: computed.display,
		flex: computed.flex,
		gridArea: computed.gridArea,
		justifySelf: computed.justifySelf,
		margin: computed.margin,
		order: computed.order,
		overflow: computed.overflow,
		padding: computed.padding,
	}
}

/**
 * A hidden, same-box placeholder holding `rect`'s layout slot while the real
 * element it stands in for is promoted out of flow. `marker` is the calling
 * file's own data-attribute name, stamped with an empty value — distinct per
 * caller (`data-schmancy-absorb-seat` vs. `data-schmancy-carry-seat`) purely
 * so a reader inspecting the DOM, or a test cleaning up after itself, can
 * tell which mechanism is holding a given slot open.
 */
export function createLayoutSeat(rect: DOMRect, preflight: LiftPreflight, marker: string): HTMLElement {
	const seat = document.createElement('div')
	seat.setAttribute(marker, '')
	seat.setAttribute('aria-hidden', 'true')
	Object.assign(seat.style, {
		boxSizing: 'border-box',
		width: `${rect.width}px`,
		height: `${rect.height}px`,
		margin: preflight.margin,
		alignSelf: preflight.alignSelf,
		justifySelf: preflight.justifySelf,
		gridArea: preflight.gridArea,
		flex: preflight.flex,
		order: preflight.order,
		pointerEvents: 'none',
		visibility: 'hidden',
	})
	return seat
}
